import { useQuery } from '@tanstack/react-query';
import React, { FC, useEffect, useMemo } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { CustomPageContext, TabbedPage } from '~/app/Layout';
import { ROUTE } from '~/app/routes';
import { InlineCopyInput, Loading, PageHeader } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { CardTokenRepository } from '~/repositories/CardTokenRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, Inner, Line, Text } from '~/styles';

interface Params {
  id: string;
}

export interface CardTokenProps {
  cardToken: {
    id: string;
    cardId: string;
    cardAccountId: string;
    cardProgramId: string;
    status: string;
    walletType: string;
    tokenType?: string;
    deviceType?: string;
    deviceName?: string;
    requestorName?: string;
    createdAt: Date;
  };
}

const TabNavigation = styled(TabbedPage)`
  margin: 0;
`;

const CardTokenHeaderRow = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const InlineCopyInputWrapper = styled.div`
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    width: auto;
  }
`;

const formatWalletType = (walletType: string): string => {
  if (!walletType) return '';
  return walletType
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const PageCardToken: FC<CustomPageContext> = ({ entries }) => {
  const navigate = useNavigate();
  const { currentPlatform, isSandbox } = useSessionStore();
  const { addDangerNotification } = useNotificationStore();
  const { id } = useParams<keyof Params>() as Params;

  const {
    data: cardToken,
    isLoading,
    error: cardTokenError,
  } = useQuery({
    queryKey: ['card-token', id, currentPlatform?.id, isSandbox],
    queryFn: () => CardTokenRepository.get(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (cardTokenError) {
      navigate(ROUTE.CARD_TOKENS);
      addDangerNotification({
        content: (cardTokenError as any).message || 'Failed to load card token',
      });
    }
  }, [cardTokenError, navigate, addDangerNotification]);

  const outletContext = useMemo(() => (cardToken ? { cardToken } : undefined), [cardToken]);

  const formatTitle = (): string => {
    if (cardToken?.walletType) {
      return `Card Token - ${formatWalletType(cardToken.walletType)}`;
    }
    return 'Card Token';
  };

  return (
    <Loading isLoading={isLoading} fullHeight>
      <PageHeader
        text="Card Tokens"
        backPath={ROUTE.CARD_TOKENS}
        tabs={<TabNavigation entries={entries} />}
        backButton
      />
      {id && cardToken && (
        <>
          <Inner pt={12} px="var(--page-margin)" pb={16}>
            <Flex flexDirection="column" gap={12}>
              <CardTokenHeaderRow>
                <Text as="h2" size={16} letterSpacing="tight" weight="medium" color="foreground">
                  {formatTitle()}
                </Text>

                <InlineCopyInputWrapper>
                  <InlineCopyInput label="Card Token ID" value={cardToken.id} minWidth="300px" isMonospaced />
                </InlineCopyInputWrapper>
              </CardTokenHeaderRow>
            </Flex>
          </Inner>
          <Line />
        </>
      )}
      {outletContext && <Outlet context={outletContext} />}
    </Loading>
  );
};
